import React from 'react';
import styled from 'astroturf/react';

import PremiumLock from 'js/premium/PremiumLock';
import trackClick from 'js/utils/trackClick';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import type { NavImpactPremiumBases, PremiumUpsells } from '../../types';
import { segmentFor } from './premiumSegment';

const CellWatcher = styled(ImpressionWatcher)`
  display: flex;
  flex: 0 0 auto;
  height: 100%;
`;

const CellLink = styled.a`
  @import 'stylesheets/design_system/font';
  @include body__medium;

  display: flex;
  flex: 0 0 auto;
  align-items: center;
  column-gap: 8px;
  height: 100%;
  padding: 0 20px;
  background: var(--button-primary-fill, #0645b1);
  color: var(--text-white, #ffffff);
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    background: var(--button-primary-fill-hover, #0c3b8d);
  }

  &:hover,
  &:focus,
  &:active,
  &:visited {
    color: var(--text-white, #ffffff);
    text-decoration: none;
  }
`;

export type PremiumCtaCellProps = {
  isPremium: boolean;
  hidePremiumUpsellButton?: boolean;
  showPodcastLibraryNav?: boolean;
  premiumUpsells?: PremiumUpsells;
  premiumBases: NavImpactPremiumBases;
  bucket: string;
};

const PremiumCtaCell = ({
  isPremium,
  hidePremiumUpsellButton = false,
  showPodcastLibraryNav = false,
  premiumUpsells,
  premiumBases,
  bucket,
}: PremiumCtaCellProps) => {
  if (isPremium || hidePremiumUpsellButton || showPodcastLibraryNav) {
    return null;
  }

  const segment = segmentFor(premiumBases, premiumUpsells);
  if (segment == null) {
    return null;
  }

  const handleClick = () => {
    trackClick({ source: 'nav-bar--impact-premium-cta', bucket });
    if (segment.controlClickSource != null) {
      trackClick({ source: segment.controlClickSource });
    }
  };

  return (
    <CellWatcher source={segment.source} extra={segment.extra}>
      <CellLink
        href={segment.url}
        onClick={handleClick}
        data-testid="premium-cta-cell"
      >
        <PremiumLock circleColor="white" lockColor="blue" width={20} />
        {segment.copy}
      </CellLink>
    </CellWatcher>
  );
};

export default PremiumCtaCell;
